import sodiumModule from "libsodium-wrappers-sumo";
import { readWebE2EEIdentityKey } from "./e2eeIdentityKeyStoreWeb";

export type WebWrappedContentKeyV1 = {
  version: 1;
  algorithm: "crypto_box_seal-x25519-xsalsa20poly1305";
  recipientUserId: string;
  recipientKeyId: string;
  senderKeyId: string;
  wrappedKeyBase64: string;
};

export type WebWrappedPrivatePayloadEnvelopeV1 = {
  envelopeVersion?: number;
  algorithm: "aes-256-cbc+hmac-sha256+wrapped-content-key-v1";
  keyWrapAlgorithm: "crypto_box_seal-x25519-xsalsa20poly1305";
  ciphertext: string;
  nonce: string;
  senderKeyId?: string;
  recipientScope: string;
  contentType: string;
  aad: any;
  wrappedContentKeys: WebWrappedContentKeyV1[];
  macBase64: string;
};

async function sodium() {
  const s: any = sodiumModule as any;
  if (s.ready) await s.ready;
  return s;
}

function base64ToBytes(value: string): Uint8Array {
  const bin = atob(String(value || ""));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToUtf8(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes);
}

function utf8ToBytes(value: string): Uint8Array {
  return new TextEncoder().encode(String(value || ""));
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

function timingSafeEqualString(a: string, b: string) {
  const left = String(a || "");
  const right = String(b || "");
  if (left.length !== right.length) return false;

  let diff = 0;
  for (let i = 0; i < left.length; i += 1) {
    diff |= left.charCodeAt(i) ^ right.charCodeAt(i);
  }
  return diff === 0;
}

function buildE2EEAAD(aad: any) {
  const value = aad || {};

  return {
    route: String(value.route || "").trim(),
    entityId: String(value.entityId || "").trim(),
    conversationId: value.conversationId ? String(value.conversationId).trim() : undefined,
    groupId: value.groupId ? String(value.groupId).trim() : undefined,
    senderUserId: value.senderUserId ? String(value.senderUserId).trim() : undefined,
    recipientScope: value.recipientScope,
    contentType: value.contentType,
    version: Number(value.version || 1),
  };
}

// Must match mobile/backend serializeE2EEAAD exactly.
function serializeE2EEAAD(aad: any): string {
  const value = buildE2EEAAD(aad || { route: "", entityId: "" });

  return [
    `route=${value.route}`,
    `entityId=${value.entityId}`,
    `conversationId=${value.conversationId || ""}`,
    `groupId=${value.groupId || ""}`,
    `senderUserId=${value.senderUserId || ""}`,
    `recipientScope=${value.recipientScope || ""}`,
    `contentType=${value.contentType || ""}`,
    `version=${value.version || 1}`,
  ].join("\n");
}

function contentMacInput(args: {
  aad: any;
  ivBase64: string;
  ciphertextBase64: string;
  contentType: string;
  recipientScope: string;
  wrappedContentKeys: WebWrappedContentKeyV1[];
}) {
  return [
    "intimsync.private-payload.wrapped-v1",
    args.contentType,
    args.recipientScope,
    args.ivBase64,
    args.ciphertextBase64,
    serializeE2EEAAD(args.aad),
    JSON.stringify(args.wrappedContentKeys),
  ].join("|");
}

function webCryptoBufferSource(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer as ArrayBuffer;
}

async function hmacSha256Base64(keyBytes: Uint8Array, value: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    "raw",
    webCryptoBufferSource(keyBytes),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );

  const sig = await crypto.subtle.sign("HMAC", key, webCryptoBufferSource(utf8ToBytes(value)));
  return bytesToBase64(new Uint8Array(sig));
}

async function aesCbcDecryptPkcs7(args: {
  keyBytes: Uint8Array;
  ivBytes: Uint8Array;
  ciphertextBytes: Uint8Array;
}): Promise<string> {
  const key = await crypto.subtle.importKey(
    "raw",
    webCryptoBufferSource(args.keyBytes),
    { name: "AES-CBC" },
    false,
    ["decrypt"]
  );

  const plaintextWithPadding = await crypto.subtle.decrypt(
    { name: "AES-CBC", iv: webCryptoBufferSource(args.ivBytes) },
    key,
    webCryptoBufferSource(args.ciphertextBytes)
  );

  // WebCrypto AES-CBC validates/removes PKCS#7 padding before returning plaintext.
  return bytesToUtf8(new Uint8Array(plaintextWithPadding));
}


export function webWrappedPayloadHasLocalRecipientKey(envelope: {
  wrappedContentKeys?: WebWrappedContentKeyV1[];
}): boolean {
  const identity = readWebE2EEIdentityKey();
  if (!identity?.keyId) return false;

  return (envelope.wrappedContentKeys || []).some((item) => item.recipientKeyId === identity.keyId);
}

export async function decryptWebWrappedPrivatePayload(args: {
  envelope: WebWrappedPrivatePayloadEnvelopeV1;
  recipientKeyId?: string;
}): Promise<string> {
  const envelope = args.envelope;

  if (envelope.algorithm !== "aes-256-cbc+hmac-sha256+wrapped-content-key-v1") {
    throw new Error("web_wrapped_payload_unsupported_algorithm");
  }

  if (envelope.keyWrapAlgorithm !== "crypto_box_seal-x25519-xsalsa20poly1305") {
    throw new Error("web_wrapped_payload_unsupported_key_wrap");
  }

  const identity = readWebE2EEIdentityKey();
  if (!identity) throw new Error("web_e2ee_identity_key_missing");

  const wrapped = (envelope.wrappedContentKeys || []).find((item) => {
    if (args.recipientKeyId) return item.recipientKeyId === args.recipientKeyId;
    return item.recipientKeyId === identity.keyId;
  });

  if (!wrapped) {
    const available = (envelope.wrappedContentKeys || [])
      .map((item) => item.recipientKeyId)
      .filter(Boolean)
      .join(",");
    throw new Error(`web_wrapped_payload_no_matching_wrapped_key:local=${identity.keyId}:available=${available}`);
  }

  const s = await sodium();
  const publicKey = s.from_base64(identity.publicKeyBase64, s.base64_variants.ORIGINAL);
  const privateKey = s.from_base64(identity.privateKeyBase64, s.base64_variants.ORIGINAL);
  const sealed = s.from_base64(wrapped.wrappedKeyBase64, s.base64_variants.ORIGINAL);

  const opened = s.crypto_box_seal_open(sealed, publicKey, privateKey);
  const contentKeyBytes = opened instanceof Uint8Array ? opened : new Uint8Array(Array.from(opened || []));

  if (contentKeyBytes.length !== 32) {
    throw new Error(`web_wrapped_payload_invalid_content_key_length:${contentKeyBytes.length}`);
  }

  const ciphertextBase64 = String(envelope.ciphertext || "").trim();
  const ivBase64 = String(envelope.nonce || "").trim();

  const macInput = contentMacInput({
    aad: envelope.aad,
    ivBase64,
    ciphertextBase64,
    contentType: envelope.contentType,
    recipientScope: envelope.recipientScope,
    wrappedContentKeys: envelope.wrappedContentKeys || [],
  });

  const actualMacBase64 = await hmacSha256Base64(contentKeyBytes, macInput);
  if (!timingSafeEqualString(actualMacBase64, String(envelope.macBase64 || "").trim())) {
    throw new Error("web_wrapped_payload_mac_failed");
  }

  return aesCbcDecryptPkcs7({
    keyBytes: contentKeyBytes,
    ivBytes: base64ToBytes(ivBase64),
    ciphertextBytes: base64ToBytes(ciphertextBase64),
  });
}

export function parseWebEncryptedFlirtPlaintext(value: string): {
  text: string;
  mediaRequest?: any;
  mediaOffer?: any;
} {
  const text = String(value || "");
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === "object") {
      return {
        text: typeof parsed.text === "string" ? parsed.text : text,
        mediaRequest: parsed.mediaRequest || undefined,
        mediaOffer: parsed.mediaOffer || undefined,
      };
    }
  } catch {
    // plain encrypted text flirt
  }

  return { text };
}
